import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, PlugZap, Plus, Unplug } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import type { Platform, Store } from "../lib/types";
import { dateTime, platformLabel, statusLabel } from "../lib/format";
import { Modal } from "../components/Modal";
import { StatusBadge, statusTone } from "../components/StatusBadge";
import { useI18n } from "../lib/i18n";

export function StoresPage() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [testNotice, setTestNotice] = useState<{ ok: boolean; message: string } | null>(null);
  const [form, setForm] = useState({ platform: "amazon" as Platform, name: "", region: "US", externalShopId: "" });
  const [authForm, setAuthForm] = useState({ platform: "amazon" as Platform, storeName: "", region: "US" });
  const query = useQuery({ queryKey: ["stores"], queryFn: () => api.get<{ stores: Store[] }>("/api/stores") });
  const mutation = useMutation({
    mutationFn: () => api.post("/api/stores", form),
    onSuccess: async () => {
      setOpen(false);
      setForm({ platform: "amazon", name: "", region: "US", externalShopId: "" });
      await queryClient.invalidateQueries({ queryKey: ["stores"] });
    }
  });
  const authMutation = useMutation({
    mutationFn: () => api.post<{ authUrl: string }>(`/api/oauth/${authForm.platform}/start`, {
      storeName: authForm.storeName,
      region: authForm.region
    }),
    onSuccess: (result) => {
      window.location.assign(result.authUrl);
    }
  });
  const testMutation = useMutation({
    mutationFn: (storeId: string) =>
      api.post<{ ok: boolean; message: string }>(`/api/stores/${storeId}/test-connection`, {}),
    onSuccess: (result) => setTestNotice({ ok: result.ok, message: t(result.message) }),
    onError: (error) => setTestNotice({ ok: false, message: error.message })
  });
  const disconnectMutation = useMutation({
    mutationFn: (storeId: string) => api.post(`/api/stores/${storeId}/disconnect`, {}),
    onSuccess: async () => {
      setTestNotice({ ok: true, message: t("已断开店铺授权") });
      await queryClient.invalidateQueries({ queryKey: ["stores"] });
    }
  });

  const oauthState = searchParams.get("oauth");
  const oauthMessage = searchParams.get("message");

  return (
    <div className="page-stack">
      {oauthState ? (
        <div className={oauthState === "success" ? "notice success-notice" : "notice error-notice"}>
          {oauthState === "success" ? t("授权成功，店铺已创建") : `${t("授权失败")}: ${oauthMessage ?? ""}`}
        </div>
      ) : null}
      {testNotice ? (
        <div className={testNotice.ok ? "notice success-notice" : "notice error-notice"}>
          {testNotice.message}
        </div>
      ) : null}
      <div className="toolbar-row">
        <div className="filter-row">
          <span className="muted">{t("共 {count} 个店铺", { count: query.data?.stores.length ?? 0 })}</span>
        </div>
        <div className="button-row">
          <button type="button" className="ghost-button" onClick={() => setAuthOpen(true)}>
            <KeyRound size={16} />
            {t("一键授权")}
          </button>
          <button type="button" className="primary-button" onClick={() => setOpen(true)}>
            <Plus size={16} />
            {t("新增店铺")}
          </button>
        </div>
      </div>
      <div className="panel table-panel">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("平台")}</th>
                <th>{t("店铺名称")}</th>
                <th>{t("区域")}</th>
                <th>{t("平台店铺 ID")}</th>
                <th>{t("状态")}</th>
                <th>{t("最近同步")}</th>
                <th>{t("操作")}</th>
              </tr>
            </thead>
            <tbody>
              {(query.data?.stores ?? []).map((store) => (
                <tr key={store.id}>
                  <td><span className={`platform-dot dot-${store.platform}`} />{platformLabel(store.platform)}</td>
                  <td><strong>{store.name}</strong></td>
                  <td>{store.region}</td>
                  <td className="mono">{store.external_shop_id || "-"}</td>
                  <td><StatusBadge tone={statusTone(store.status)}>{t(statusLabel(store.status))}</StatusBadge></td>
                  <td>{dateTime(store.last_sync_at)}</td>
                  <td>
                    <div className="button-row">
                      <button
                        type="button"
                        className="ghost-button compact-button"
                        disabled={testMutation.isPending}
                        onClick={() => {
                          setTestNotice(null);
                          testMutation.mutate(store.id);
                        }}
                      >
                        <PlugZap size={15} />
                        {testMutation.isPending ? t("测试中...") : t("测试连接")}
                      </button>
                      <button
                        type="button"
                        className="ghost-button compact-button danger-button"
                        disabled={disconnectMutation.isPending || store.status === "disabled"}
                        onClick={() => {
                          if (window.confirm(t("确认断开该店铺授权？"))) {
                            disconnectMutation.mutate(store.id);
                          }
                        }}
                      >
                        <Unplug size={15} />
                        {t("断开授权")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} title={t("新增店铺")} onClose={() => setOpen(false)}>
        <div className="form-stack">
          <label className="field">
            <span>{t("平台")}</span>
            <select value={form.platform} onChange={(event) => setForm({ ...form, platform: event.target.value as Platform })}>
              <option value="amazon">Amazon</option>
              <option value="tiktok">TikTok Shop</option>
              <option value="shein">SHEIN</option>
            </select>
          </label>
          <label className="field">
            <span>{t("店铺名称")}</span>
            <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="例如 Amazon US Store" />
          </label>
          <label className="field">
            <span>{t("区域")}</span>
            <input value={form.region} onChange={(event) => setForm({ ...form, region: event.target.value })} placeholder="US / EU / SG" />
          </label>
          <label className="field">
            <span>{t("平台店铺 ID")}</span>
            <input value={form.externalShopId} onChange={(event) => setForm({ ...form, externalShopId: event.target.value })} placeholder="可选" />
          </label>
          {mutation.isError ? <div className="form-error">{mutation.error.message}</div> : null}
          <div className="modal-actions">
            <button type="button" className="ghost-button" onClick={() => setOpen(false)}>{t("取消")}</button>
            <button type="button" className="primary-button" disabled={!form.name || mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? t("保存中...") : t("保存店铺")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={authOpen} title={t("一键授权店铺")} onClose={() => setAuthOpen(false)}>
        <div className="form-stack">
          <label className="field">
            <span>{t("平台")}</span>
            <select value={authForm.platform} onChange={(event) => setAuthForm({ ...authForm, platform: event.target.value as Platform })}>
              <option value="amazon">Amazon</option>
              <option value="tiktok">TikTok Shop</option>
              <option value="shein">SHEIN</option>
            </select>
          </label>
          <label className="field">
            <span>{t("店铺名称")}</span>
            <input value={authForm.storeName} onChange={(event) => setAuthForm({ ...authForm, storeName: event.target.value })} placeholder="例如 Amazon US Store" />
          </label>
          <label className="field">
            <span>{t("区域")}</span>
            <input value={authForm.region} onChange={(event) => setAuthForm({ ...authForm, region: event.target.value })} placeholder="US / EU / SG" />
          </label>
          <p className="muted">{t("点击开始后会跳转到平台官方页面，完成登录和授权后自动创建店铺。")}</p>
          {authMutation.isError ? <div className="form-error">{authMutation.error.message}</div> : null}
          <div className="modal-actions">
            <button type="button" className="ghost-button" onClick={() => setAuthOpen(false)}>{t("取消")}</button>
            <button type="button" className="primary-button" disabled={!authForm.storeName || authMutation.isPending} onClick={() => authMutation.mutate()}>
              {authMutation.isPending ? t("授权中...") : t("开始授权")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
